import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { firmIntelligenceUrl, firmScheduleOfInvestmentsUrl } from 'common/config/urls';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { useSecondaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import type { SecondaryNavigationLinkItem } from 'context/FirmBaseLayoutContext';
import { SummaryDefinitionsProvider } from 'context/SummaryDefinitionsContext';
import { FundDialogForm } from 'pages/Funds/forms';
import { useDialogFormsStore } from 'store';
import { getObjectValue } from 'utilities';
import FirmSummaryFundFilterMenu from './FirmSummaryFundFilterMenu';
import FirmSummaryWorkspace from './FirmSummaryWorkspace';
import { IntelligenceProvider } from './IntelligenceContext';
import {
  INTELLIGENCE_DEFAULT_SECONDARY_NAVIGATION_SLUG,
  INTELLIGENCE_DEFAULT_TERTIARY_NAVIGATION_SLUG,
  INTELLIGENCE_SECONDARY_NAVIGATION_LABELS,
  INTELLIGENCE_SECONDARY_NAVIGATION_SLUGS,
} from './intelligenceNavigation.constants';
import ScheduleOfInvestments from './ScheduleOfInvestments';
import useFirmSummaryFundFilter from './useFirmSummaryFundFilter';

type IntelligenceRouteParams = {
  firmSlugParam: string;
  secondaryTabSlugParam: string;
  tertiaryTabSlugParam: string;
};

interface IntelligenceContentProps {
  firmSlug: string;
  activeSecondaryTabSlug: string;
  activeTertiaryTabSlug: string;
}

const IntelligenceContent = ({
  firmSlug,
  activeSecondaryTabSlug,
  activeTertiaryTabSlug,
}: IntelligenceContentProps) => {
  const { search } = useLocation();
  const { titleAccessory, ...menuProps } = useFirmSummaryFundFilter();
  const displayFundDialogForm = useDialogFormsStore(state => state.displayFundDialogForm);
  const setDisplayFundDialogForm = useDialogFormsStore(state => state.setDisplayFundDialogForm);
  const handleCloseFundDialog = useCallback(() => setDisplayFundDialogForm(false), [setDisplayFundDialogForm]);

  const secondaryNavigationItems = useMemo<SecondaryNavigationLinkItem[]>(
    // Carry sticky filters (?fund=, ?date=) across tab changes so the user doesn't lose context.
    () => [
      {
        label: INTELLIGENCE_SECONDARY_NAVIGATION_LABELS.SUMMARIES,
        value: INTELLIGENCE_SECONDARY_NAVIGATION_SLUGS.SUMMARIES,
        to: `${firmIntelligenceUrl(firmSlug)}/${INTELLIGENCE_SECONDARY_NAVIGATION_SLUGS.SUMMARIES}/${INTELLIGENCE_DEFAULT_TERTIARY_NAVIGATION_SLUG}${search}`,
      },
      {
        label: INTELLIGENCE_SECONDARY_NAVIGATION_LABELS.SCHEDULE_OF_INVESTMENTS,
        value: INTELLIGENCE_SECONDARY_NAVIGATION_SLUGS.SCHEDULE_OF_INVESTMENTS,
        to: `${firmScheduleOfInvestmentsUrl(firmSlug)}${search}`,
      },
    ],
    [firmSlug, search]
  );

  const secondaryNavigation = useMemo(
    () => ({
      title: 'Intelligence',
      titleAccessory,
      items: secondaryNavigationItems,
      // Pages with nested sub-routes need to override the host's URL-based active matching
      // so the Summaries tab stays highlighted under any tertiary slug.
      value: activeSecondaryTabSlug,
    }),
    [activeSecondaryTabSlug, secondaryNavigationItems, titleAccessory]
  );

  useSecondaryNavigationRegistration(secondaryNavigation);

  const isScheduleOfInvestments =
    activeSecondaryTabSlug === INTELLIGENCE_SECONDARY_NAVIGATION_SLUGS.SCHEDULE_OF_INVESTMENTS;

  return (
    <>
      {isScheduleOfInvestments ? (
        <ScheduleOfInvestments />
      ) : (
        <FirmSummaryWorkspace firmSlug={firmSlug} activeTertiaryTabSlug={activeTertiaryTabSlug} />
      )}
      <FirmSummaryFundFilterMenu {...menuProps} />
      <FundDialogForm open={displayFundDialogForm} handleOnClose={handleCloseFundDialog} onSuccess={handleCloseFundDialog} />
    </>
  );
};

const SECONDARY_SLUGS = Object.values(INTELLIGENCE_SECONDARY_NAVIGATION_SLUGS) as string[];

const Intelligence = () => {
  const { firmSlugParam, secondaryTabSlugParam, tertiaryTabSlugParam } = useParams<IntelligenceRouteParams>();
  const [storeValue] = useStore() as UseStoreValues;
  const { firmInfo } = getObjectValue(storeValue);
  const firmId = getObjectValue(firmInfo).id as number | undefined;

  const navigate = useNavigate();
  const location = useLocation();

  // Legacy URLs (e.g. /intelligence/firm-summary, /intelligence/view-3) live one level
  // shallower than the current shape; promote them to /intelligence/summaries/<old slug>.
  const isLegacyShape =
    Boolean(secondaryTabSlugParam) && !SECONDARY_SLUGS.includes(secondaryTabSlugParam as string);

  useEffect(() => {
    if (!isLegacyShape || !firmSlugParam) {
      return;
    }
    navigate(
      {
        pathname: `${firmIntelligenceUrl(firmSlugParam)}/${INTELLIGENCE_DEFAULT_SECONDARY_NAVIGATION_SLUG}/${secondaryTabSlugParam}`,
        search: location.search,
      },
      { replace: true }
    );
  }, [firmSlugParam, isLegacyShape, location.search, navigate, secondaryTabSlugParam]);

  if (!firmSlugParam || !firmId || isLegacyShape) {
    return null;
  }

  const activeSecondaryTabSlug = secondaryTabSlugParam ?? INTELLIGENCE_DEFAULT_SECONDARY_NAVIGATION_SLUG;
  const activeTertiaryTabSlug = tertiaryTabSlugParam ?? INTELLIGENCE_DEFAULT_TERTIARY_NAVIGATION_SLUG;

  return (
    <SummaryDefinitionsProvider entityId={firmId} summaryType="firm" fetchEnabled>
      <IntelligenceProvider>
        <IntelligenceContent
          firmSlug={firmSlugParam}
          activeSecondaryTabSlug={activeSecondaryTabSlug}
          activeTertiaryTabSlug={activeTertiaryTabSlug}
        />
      </IntelligenceProvider>
    </SummaryDefinitionsProvider>
  );
};

export default Intelligence;
